import React from "react";
import { formatNZDate, formatNZDateTime } from "../../utils/timezone";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import {
  Eye, Star, Trash2, Percent, Pause, Play, ChevronRight, ChevronDown, MapPin, Key
} from "lucide-react";

export function AdminShopsTab({
  shops,
  expandedShopId,
  setExpandedShopId,
  onImpersonateShop,
  onOpenCommissionDialog,
  onOpenGoogleRatingDialog,
  onUnsuspendShop,
  onSuspendShop,
  onDeleteShop,
  onSetTempPassword,
  onConvertToFranchise,
  onGeocodeShop,
  onGeocodeAll,
}) {
  const missingCoordsCount = shops.filter(
    (s) => !s.address || s.address.latitude == null || s.address.longitude == null
  ).length;
  return (
    <Card className="glass-card">
      <CardHeader>
        <div className="flex items-start justify-between flex-wrap gap-2">
          <div>
            <CardTitle>Registered Shops</CardTitle>
            <CardDescription>All tyre shops on the platform - manage commission rates</CardDescription>
          </div>
          {missingCoordsCount > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onGeocodeAll && onGeocodeAll()}
              data-testid="geocode-all-missing-shops-btn"
              className="text-amber-600 border-amber-600 hover:bg-amber-50 dark:hover:bg-amber-950/20"
              title="Backfill latitude/longitude for every shop missing coordinates. Without coords, the shop is invisible to all quote requests."
            >
              <MapPin className="h-4 w-4 mr-1" />
              Fix {missingCoordsCount} shops missing coords
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {/* MOBILE: stacked card list. Hidden on md+. */}
        <div className="md:hidden space-y-3">
          {shops.map((shop) => {
            const missingCoords = !shop.address || shop.address.latitude == null || shop.address.longitude == null;
            const isExpanded = expandedShopId === shop.shop_id;
            return (
              <div
                key={`m-${shop.shop_id}`}
                data-testid={`shop-mobile-card-${shop.shop_id}`}
                className="rounded-lg border border-border bg-card p-3 space-y-2"
              >
                <div
                  className="flex items-start justify-between gap-2 cursor-pointer"
                  onClick={() => setExpandedShopId(isExpanded ? null : shop.shop_id)}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1 font-medium">
                      {isExpanded ? <ChevronDown className="h-4 w-4 flex-shrink-0" /> : <ChevronRight className="h-4 w-4 flex-shrink-0" />}
                      <span className="truncate">{shop.shop_name}</span>
                    </div>
                    {shop.business_type === "franchise" && shop.owner_business_name && (
                      <div className="text-[10px] text-muted-foreground ml-5">Franchise: {shop.owner_business_name}</div>
                    )}
                    <div className="text-xs text-muted-foreground ml-5 truncate">
                      {shop.address?.suburb || shop.address?.city || (typeof shop.address === "string" ? shop.address : "—")}
                    </div>
                  </div>
                  <Badge
                    className={
                      shop.approval_status === "approved"
                        ? "bg-green-500/10 text-green-600 border-green-500/30"
                        : shop.approval_status === "suspended"
                        ? "bg-red-500/10 text-red-600 border-red-500/30"
                        : "bg-yellow-500/10 text-yellow-600 border-yellow-500/30"
                    }
                  >
                    {shop.approval_status}
                  </Badge>
                </div>

                <div className="grid grid-cols-4 gap-2 text-center text-xs ml-5">
                  <div>
                    <div className="text-muted-foreground">Avail.</div>
                    <div className={`font-medium ${shop.available_requests_count > 0 ? "text-blue-600" : "text-muted-foreground"}`}>
                      {shop.available_requests_count || 0}
                    </div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Quotes</div>
                    <div className="font-medium">{shop.total_quotes || 0}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Orders</div>
                    <div className="font-medium">{shop.total_orders || 0}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Comm.</div>
                    <div className="font-medium">{((shop.commission_rate || 0) * 100).toFixed(0)}%</div>
                  </div>
                </div>

                {missingCoords && (
                  <div className="ml-5">
                    <Badge variant="outline" className="text-amber-600 border-amber-600 bg-amber-50/40 dark:bg-amber-950/10 text-[10px]">
                      No coords — invisible to requests
                    </Badge>
                  </div>
                )}

                <div className="flex flex-wrap gap-1.5 ml-5">
                  <Button size="sm" variant="ghost" className="h-8 px-2" onClick={(e) => { e.stopPropagation(); onImpersonateShop(shop.shop_id); }} data-testid={`m-view-shop-${shop.shop_id}`}>
                    <Eye className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="outline" className="h-8 px-2" onClick={(e) => { e.stopPropagation(); onOpenCommissionDialog(shop); }} title="Set commission" data-testid={`m-set-commission-${shop.shop_id}`}>
                    <Percent className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="outline" className="h-8 px-2" onClick={(e) => { e.stopPropagation(); onOpenGoogleRatingDialog(shop); }} title="Google rating">
                    <Star className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="outline" className="h-8 px-2" onClick={(e) => { e.stopPropagation(); onSetTempPassword(shop); }} title="Temp password" data-testid={`m-set-temp-password-${shop.shop_id}`}>
                    <Key className="h-4 w-4" />
                  </Button>
                  {shop.approval_status === "suspended" ? (
                    <Button size="sm" variant="outline" className="h-8 px-2 text-green-600 border-green-600" onClick={(e) => { e.stopPropagation(); onUnsuspendShop(shop); }} title="Unsuspend">
                      <Play className="h-4 w-4" />
                    </Button>
                  ) : shop.approval_status === "approved" && (
                    <Button size="sm" variant="outline" className="h-8 px-2 text-orange-600 border-orange-600" onClick={(e) => { e.stopPropagation(); onSuspendShop(shop); }} title="Suspend">
                      <Pause className="h-4 w-4" />
                    </Button>
                  )}
                  {missingCoords && (
                    <Button size="sm" variant="outline" className="h-8 px-2 text-amber-600 border-amber-600" onClick={(e) => { e.stopPropagation(); onGeocodeShop && onGeocodeShop(shop); }} title="Geocode address" data-testid={`m-geocode-shop-${shop.shop_id}`}>
                      <MapPin className="h-4 w-4" />
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" className="h-8 px-2 text-destructive ml-auto" onClick={(e) => { e.stopPropagation(); onDeleteShop(shop); }} title="Delete">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>

                {isExpanded && (
                  <div className="ml-5 pt-2 border-t border-border/50 grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <span className="text-muted-foreground">Email</span>
                      <div className="font-medium break-all">{shop.email || "N/A"}</div>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Phone</span>
                      <div className="font-medium">{shop.phone || "N/A"}</div>
                    </div>
                    <div className="col-span-2">
                      <span className="text-muted-foreground">Address</span>
                      <div className="font-medium">{shop.address?.full_address || (typeof shop.address === "string" ? shop.address : "N/A")}</div>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Logins</span>
                      <div className="font-medium">{shop.login_count || 0}</div>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Last Login</span>
                      <div className="font-medium">{shop.last_login ? formatNZDateTime(shop.last_login) : "Never"}</div>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Joined</span>
                      <div className="font-medium">{shop.join_date ? formatNZDate(shop.join_date) : "—"}</div>
                    </div>
                    {shop.business_type !== "franchise" && (
                      <div className="col-span-2 pt-1">
                        <Button size="sm" variant="outline" className="w-full" onClick={(e) => { e.stopPropagation(); onConvertToFranchise && onConvertToFranchise(shop); }}>
                          Convert to Franchise
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          {shops.length === 0 && (
            <div className="text-center text-sm text-muted-foreground py-8">No shops yet.</div>
          )}
        </div>

        {/* DESKTOP: full table. Hidden on mobile.
            The shop-name TH/TD is `sticky left-0` so it stays pinned while
            the rest of the columns scroll horizontally — fixes the "I can't
            tell which shop I'm editing when scrolled right" pain point. */}
        <div className="overflow-x-auto hidden md:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="sticky left-0 z-20 bg-background shadow-[2px_0_4px_-2px_rgba(0,0,0,0.08)] min-w-[180px]">Shop Name</TableHead>
              <TableHead>Location</TableHead>
              <TableHead>Google Rating</TableHead>
              <TableHead>Avail. Requests</TableHead>
              <TableHead>Quotes</TableHead>
              <TableHead>Orders</TableHead>
              <TableHead>Logins</TableHead>
              <TableHead>Last Login</TableHead>
              <TableHead>Commission</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {shops.map((shop) => (
              <React.Fragment key={shop.shop_id}>
              <TableRow data-testid={`shop-row-${shop.shop_id}`} className="cursor-pointer group" onClick={() => setExpandedShopId(expandedShopId === shop.shop_id ? null : shop.shop_id)}>
                <TableCell className="font-medium sticky left-0 z-10 bg-background group-hover:bg-muted/50 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.08)] min-w-[180px]">
                  <div className="flex items-center gap-2">
                    {expandedShopId === shop.shop_id ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    <div className="min-w-0">
                      <div className="truncate max-w-[150px]" title={shop.shop_name}>{shop.shop_name}</div>
                      {shop.business_type === "franchise" && shop.owner_business_name && (
                        <div className="text-[10px] text-muted-foreground truncate max-w-[150px]" data-testid={`shop-owner-label-${shop.shop_id}`} title={shop.owner_business_name}>
                          {shop.owner_business_name}
                        </div>
                      )}
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  {(() => {
                    // Prefer structured address.suburb/city; fall back to postcode for ancient rows.
                    const addr = shop.address || {};
                    const parts = [addr.suburb, addr.city].filter(Boolean);
                    if (parts.length) return <span>{parts.join(", ")}</span>;
                    if (typeof shop.address === "string" && shop.address) return <span>{shop.address}</span>;
                    return <span className="text-xs text-muted-foreground">{shop.postcode || "—"}</span>;
                  })()}
                </TableCell>
                <TableCell>
                  {shop.google_rating != null ? (
                    <div className="flex items-center gap-1.5">
                      <svg viewBox="0 0 24 24" className="h-4 w-4 flex-shrink-0" xmlns="http://www.w3.org/2000/svg">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                      </svg>
                      <span className="text-sm font-medium">{shop.google_rating.toFixed(1)}</span>
                      {shop.google_review_count != null && (
                        <span className="text-xs text-muted-foreground">({shop.google_review_count})</span>
                      )}
                    </div>
                  ) : (
                    <span className="text-xs text-muted-foreground">Not set</span>
                  )}
                </TableCell>
                <TableCell>
                  <Badge className={shop.available_requests_count > 0 ? "bg-blue-500/10 text-blue-600 border-blue-500/30" : "bg-gray-500/10 text-gray-600 border-gray-500/30"} data-testid={`shop-avail-requests-${shop.shop_id}`}>
                    {shop.available_requests_count || 0}
                  </Badge>
                  {(!shop.address || shop.address.latitude == null || shop.address.longitude == null) && (
                    <div className="mt-1">
                      <Badge
                        variant="outline"
                        className="text-amber-600 border-amber-600 bg-amber-50/40 dark:bg-amber-950/10 text-[10px]"
                        title="Missing latitude/longitude — this shop is invisible to all quote requests."
                      >
                        No coords
                      </Badge>
                    </div>
                  )}
                </TableCell>
                <TableCell>{shop.total_quotes}</TableCell>
                <TableCell>{shop.total_orders}</TableCell>
                <TableCell data-testid={`shop-logins-${shop.shop_id}`}>{shop.login_count || 0}</TableCell>
                <TableCell className="text-xs text-muted-foreground" data-testid={`shop-last-login-${shop.shop_id}`}>{shop.last_login ? formatNZDateTime(shop.last_login) : 'Never'}</TableCell>
                <TableCell>
                  <Badge className={shop.commission_rate > 0 ? "bg-green-500/10 text-green-600" : "bg-gray-500/10 text-gray-600"}>
                    <Percent className="h-3 w-3 mr-1" />
                    {((shop.commission_rate || 0) * 100).toFixed(1)}%
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); onOpenCommissionDialog(shop); }}
                      data-testid={`set-commission-${shop.shop_id}`}
                    >
                      <Percent className="h-4 w-4 mr-1" />
                      Set Rate
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); onOpenGoogleRatingDialog(shop); }}
                      data-testid={`set-google-rating-${shop.shop_id}`}
                    >
                      <Star className="h-4 w-4 mr-1" />
                      Google
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); onImpersonateShop(shop.shop_id); }}
                      data-testid={`view-shop-${shop.shop_id}`}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      View
                    </Button>
                    {shop.approval_status === "suspended" ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); onUnsuspendShop(shop); }}
                        className="text-green-600 border-green-600"
                      >
                        <Play className="h-4 w-4 mr-1" />
                        Unsuspend
                      </Button>
                    ) : shop.approval_status === "approved" && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); onSuspendShop(shop); }}
                        className="text-orange-600 border-orange-600"
                      >
                        <Pause className="h-4 w-4 mr-1" />
                        Suspend
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); onSetTempPassword(shop); }}
                      data-testid={`set-temp-password-${shop.shop_id}`}
                      title="Issue temp password"
                    >
                      <Key className="h-4 w-4 mr-1" />
                      Temp Pwd
                    </Button>
                    {(!shop.address || shop.address.latitude == null || shop.address.longitude == null) && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); onGeocodeShop && onGeocodeShop(shop); }}
                        data-testid={`geocode-shop-${shop.shop_id}`}
                        className="text-amber-600 border-amber-600 hover:bg-amber-50 dark:hover:bg-amber-950/20"
                        title="Backfill lat/long via Photon so this shop starts receiving quote requests."
                      >
                        <MapPin className="h-4 w-4 mr-1" />
                        Geocode
                      </Button>
                    )}
                    {shop.business_type !== "franchise" && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); onConvertToFranchise && onConvertToFranchise(shop); }}
                        data-testid={`convert-to-franchise-${shop.shop_id}`}
                        title="Convert this shop into a franchise owner account (in-place)"
                      >
                        Convert to Franchise
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); onDeleteShop(shop); }}
                      className="text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
              {expandedShopId === shop.shop_id && (
                <TableRow className="bg-muted/30">
                  <TableCell colSpan={10} className="p-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className="text-muted-foreground">Shop Name</p>
                        <p className="font-medium">{shop.shop_name || "N/A"}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Email</p>
                        <p className="font-medium break-all">{shop.email || "N/A"}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Phone</p>
                        <p className="font-medium">{shop.phone || "N/A"}</p>
                      </div>
                      <div className="col-span-1 sm:col-span-2 md:col-span-3">
                        <p className="text-muted-foreground">Full Address</p>
                        <p className="font-medium">
                          {typeof shop.address === 'object' ? shop.address?.full_address : shop.address || "N/A"}
                        </p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Street</p>
                        <p className="font-medium">
                          {shop.address?.street_number ? `${shop.address.street_number} ` : ""}{shop.address?.street_name || "N/A"}
                        </p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Suburb</p>
                        <p className="font-medium">{shop.address?.suburb || "N/A"}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">City</p>
                        <p className="font-medium">{shop.address?.city || "N/A"}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Region</p>
                        <p className="font-medium">{shop.address?.region || "N/A"}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Postcode</p>
                        <p className="font-medium">{shop.address?.postcode || "N/A"}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Service Radius</p>
                        <p className="font-medium">{shop.service_radius_km || 50} km</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Account Status</p>
                        <p className="font-medium capitalize">{shop.account_status || "N/A"}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Approval Status</p>
                        <Badge className={
                          shop.approval_status === "approved" ? "bg-green-500/10 text-green-600" :
                          shop.approval_status === "suspended" ? "bg-red-500/10 text-red-600" :
                          "bg-yellow-500/10 text-yellow-600"
                        }>{shop.approval_status}</Badge>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Joined</p>
                        <p className="font-medium">{shop.join_date ? formatNZDate(shop.join_date) : "N/A"}</p>
                      </div>
                      {shop.description && (
                        <div className="col-span-1 sm:col-span-2 md:col-span-3">
                          <p className="text-muted-foreground">Description</p>
                          <p className="font-medium">{shop.description}</p>
                        </div>
                      )}
                      {shop.google_rating != null && (
                        <div>
                          <p className="text-muted-foreground">Google Rating</p>
                          <p className="font-medium">{shop.google_rating.toFixed(1)} {shop.google_review_count != null ? `(${shop.google_review_count} reviews)` : ""}</p>
                        </div>
                      )}
                      {(shop.google_maps_link || shop.google_maps_url) && (
                        <div className="col-span-1 sm:col-span-2 md:col-span-3">
                          <p className="text-muted-foreground">Google Maps</p>
                          <a href={shop.google_maps_link || shop.google_maps_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-primary hover:underline break-all">
                            <MapPin className="h-4 w-4 flex-shrink-0" />
                            {shop.google_maps_link || shop.google_maps_url}
                          </a>
                        </div>
                      )}
                      {shop.pause_requested && (
                        <div className="col-span-1 sm:col-span-2 md:col-span-3">
                          <p className="text-muted-foreground">Pause Reason</p>
                          <p className="font-medium text-amber-600">{shop.pause_reason || "No reason provided"}</p>
                        </div>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              )}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
        </div>
      </CardContent>
    </Card>
  );
}
